import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { TrendingUp, Users, Package, Percent, IndianRupee } from "lucide-react";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white border border-[hsl(var(--kb-border))] p-5" data-testid={`super-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-[hsl(var(--kb-muted))] mb-1">
        <Icon size={14} /> {label}
      </div>
      <div className={`font-display text-3xl ${accent || ""}`}>{value}</div>
    </div>
  );
}

export default function SuperAdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/superadmin/analytics/revenue");
        setData(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load analytics");
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return <div className="space-y-4" data-testid="super-analytics-loading">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-24 bg-[hsl(var(--kb-surface-alt))] animate-pulse" />)}
      </div>
      <div className="h-64 bg-[hsl(var(--kb-surface-alt))] animate-pulse" />
    </div>;
  }
  if (!data) return null;

  const byPlan = data.by_line_item.filter((l) => l.type === "plan");
  const byFeature = data.by_line_item.filter((l) => l.type === "feature");
  const byUsage = data.by_line_item.filter((l) => l.type === "usage");

  return (
    <div className="space-y-6" data-testid="super-analytics-page">
      <div>
        <h1 className="font-display text-3xl">Revenue Analytics</h1>
        <p className="text-sm text-[hsl(var(--kb-muted))] mt-1">Aggregate revenue across paid invoices, broken down by plan, feature and usage.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat icon={IndianRupee} label="Paid revenue" value={`₹${data.overall.total_paid.toLocaleString()}`} accent="text-green-700" />
        <Stat icon={TrendingUp} label="Pending" value={`₹${data.overall.total_pending.toLocaleString()}`} accent="text-amber-700" />
        <Stat icon={Users} label="Tenants" value={data.total_tenants} />
        <Stat icon={Package} label="Paid invoices" value={data.overall.paid_invoice_count} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[hsl(var(--kb-border))] p-5">
          <h2 className="font-display text-xl mb-4">Revenue by plan</h2>
          {byPlan.length === 0 ? <div className="text-sm text-[hsl(var(--kb-muted))]">No paid invoices yet.</div> : (
            <ul className="space-y-2">
              {byPlan.map((r) => (
                <li key={r.key} className="flex items-center justify-between text-sm" data-testid={`rev-plan-${r.key}`}>
                  <span>{r.name}</span>
                  <span className="font-medium">₹{r.total.toLocaleString()}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white border border-[hsl(var(--kb-border))] p-5">
          <h2 className="font-display text-xl mb-4">Revenue by feature</h2>
          {byFeature.length === 0 ? <div className="text-sm text-[hsl(var(--kb-muted))]">No feature revenue yet.</div> : (
            <ul className="space-y-2">
              {byFeature.map((r) => (
                <li key={r.key} className="flex items-center justify-between text-sm" data-testid={`rev-feature-${r.key}`}>
                  <span>{r.name}</span>
                  <span className="font-medium">₹{r.total.toLocaleString()}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white border border-[hsl(var(--kb-border))] p-5">
          <h2 className="font-display text-xl mb-4">Usage-based revenue</h2>
          {byUsage.length === 0 ? <div className="text-sm text-[hsl(var(--kb-muted))]">No usage charges yet.</div> : (
            <ul className="space-y-2">
              {byUsage.map((r) => (
                <li key={r.key} className="flex items-center justify-between text-sm">
                  <span>{r.name}</span>
                  <span className="font-medium">₹{r.total.toLocaleString()}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white border border-[hsl(var(--kb-border))] p-5">
          <h2 className="font-display text-xl mb-4 flex items-center gap-2"><Percent size={18} /> Feature adoption</h2>
          {data.feature_adoption.length === 0 ? <div className="text-sm text-[hsl(var(--kb-muted))]">No add-on subscriptions yet.</div> : (
            <ul className="space-y-2">
              {data.feature_adoption.map((r) => (
                <li key={r.feature_key} className="text-sm" data-testid={`adoption-${r.feature_key}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs">{r.feature_key}</span>
                    <span>{r.tenant_count}/{data.total_tenants} · {r.adoption_rate}%</span>
                  </div>
                  <div className="w-full bg-[hsl(var(--kb-surface-alt))] h-1 mt-1">
                    <div className="h-1 bg-[hsl(var(--kb-primary))]" style={{ width: `${Math.min(100, r.adoption_rate)}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
